import { z } from 'zod'
import { dateOrIsoString } from '~/prix/types/date'
import { enumerated } from '~/prix/types/enumerated'
import sql from '~/prix/utils/sql'
import { citizenObject } from '~/packages/general/citizen/citizenObject'
import { veterinarianObject } from '../veterinarian/veterinarianObject'
import { veterinaryExamObject } from '../veterinaryExam/veterinaryExamObject'
import { veterinarySymptomObject } from '../veterinarySymptom/veterinarySymptomObject'
import { veterinaryMedicationObject } from '../veterinaryMedication/veterinaryMedicationObject'

export const veterinaryCareType = enumerated({
  consultation: 'Consulta',
  follow_up: 'Retorno',
  vaccination: 'Vacinação',
  deworming: 'Vermifugação',
  checkup: 'Check-up',
  emergency: 'Emergência',
  exams: 'Exames',
  surgery: 'Cirurgia',
  neutering: 'Castração',
  dressing: 'Curativo',
  monitoring: 'Acompanhamento',
  guidance: 'Orientação',
  other: 'Outro',
})

export const veterinaryCareTypeOptions = (veterinaryCareType.getMeta()?.keysAndLabels ??
  {}) as Record<string, string>

export const veterinaryCareObject = z.object({
  id: z.string().uuid(),
  code: z.number().int().positive().nullish(),
  serviceDate: dateOrIsoString(),
  veterinarianId: veterinarianObject.shape.id,
  species: z.string(),
  type: veterinaryCareType,
  diagnosis: z.string().nullish(),
  recommendations: z.string().nullish(),
  observations: z.string().nullish(),
  sourceFilter: z.string().nullish(),
  closedAt: dateOrIsoString().nullish(),
  createdAt: dateOrIsoString().nullish(),
  updatedAt: dateOrIsoString().nullish(),
  deletedAt: dateOrIsoString().nullish(),
})

export const veterinaryCareCitizenSummaryObject = citizenObject.pick({
  id: true,
  fullName: true,
  socialName: true,
})

export const veterinaryCareVeterinarianSummaryObject = veterinarianObject
  .pick({
    id: true,
    code: true,
    citizenId: true,
  })
  .extend({
    citizen: veterinaryCareCitizenSummaryObject.nullish(),
  })

export const veterinaryCareProtocolSummaryObject = z.object({
  id: z.string().uuid(),
  code: z.number().int().positive(),
  citizenId: z.string().uuid().nullish(),
})

export const veterinaryCareListItemObject = veterinaryCareObject.extend({
  protocol: veterinaryCareProtocolSummaryObject.nullish(),
  citizen: veterinaryCareCitizenSummaryObject.nullish(),
  veterinarian: veterinaryCareVeterinarianSummaryObject.nullish(),
  exams: z.array(veterinaryExamObject),
  symptoms: z.array(veterinarySymptomObject),
  medications: z.array(veterinaryMedicationObject),
})

export const veterinaryCareFilterObject = veterinaryCareObject.extend({
  citizenId: citizenObject.shape.id.nullish(),
})

export type VeterinaryCareType = z.infer<typeof veterinaryCareObject>
export type VeterinaryCareListItemType = z.infer<typeof veterinaryCareListItemObject>

export const propertyMap = {
  id: sql`veterinary_care.id`,
  code: sql`protocol.code`,
  serviceDate: sql`veterinary_care.service_date`,
  veterinarianId: sql`veterinary_care.veterinarian_id`,
  citizenId: sql`protocol.citizen_id`,
  species: sql`veterinary_care.species`,
  type: sql`veterinary_care.type`,
  diagnosis: sql`veterinary_care.diagnosis`,
  recommendations: sql`veterinary_care.recommendations`,
  observations: sql`veterinary_care.observations`,
  closedAt: sql`veterinary_care.closed_at`,
  createdAt: sql`veterinary_care.created_at`,
  updatedAt: sql`veterinary_care.updated_at`,
  deletedAt: sql`veterinary_care.deleted_at`,
}
